import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { IOpsViewerMessagesService } from './iOpsViewerMessages.service';
import moment from 'moment';

@Injectable()
export class OpsViewerMessagesService extends IOpsViewerMessagesService {

  private _key = 'global-messages';

  constructor(
    private _messageService: MessageService) {
    super();
  }

  errorMessage(error: any, summaryMessage: string): void {
    this._messageService.clear();

    let errorAsString = JSON.stringify(error);

    // Capture DateTime and Tracking Id in message
    const dateString: string = moment().format('MM-DD-YYYY HH:mm');
    const trackingIdString: string = Math.random().toString(36).substring(7);
    errorAsString = errorAsString + ` Date: ${dateString}, Error Message Id:: ${trackingIdString}`;

    // Display user friendly error in message banner
    // If validation message it will be in inner error.Message property
    let errorMessage: string = errorAsString;
    const parsedError = this.tryParseError(error);
    if (parsedError != null) {
      errorMessage = `${parsedError} Date: ${dateString}, Error Message Id:: ${trackingIdString}`;
    } else if (error.message) {
      errorMessage = error.message + ` Date: ${dateString}, Error Message Id:: ${trackingIdString}`;
    }
    this._messageService.add({key: this._key, severity: 'error', sticky:true, summary: summaryMessage, detail: errorMessage });

    // Log error to app insights as string value
    this.sendToApplicationInsights(errorAsString, trackingIdString);
    console.error(error);
  }

  sendToApplicationInsights(errorAsString:string, trackingIdString :string){
    if (errorAsString.includes("Validation Error:")){//do not include validation errors
     // this._appInsightsService.trackTrace(errorAsString, { "trackingId" : trackingIdString}, SeverityLevel.Warning);
    } else {
     // this._appInsightsService.trackException(new Error(errorAsString), null, { "trackingId" : trackingIdString});
    }
  }

  errorMessageWithObjectDetail(error: any, summaryMessage: string, serializedObject: string): void {
    this._messageService.clear();

    let errorAsString = JSON.stringify(error);

    // Capture DateTime and Tracking Id in message
    const dateString: string = moment().format('MM-DD-YYYY HH:mm');
    const trackingIdString: string = Math.random().toString(36).substring(7);
    errorAsString = errorAsString + ` Date: ${dateString}, Error Message Id:: ${trackingIdString}`;

    // Display user friendly error in message banner
    // If validation message it will be in inner error.Message property
    let errorMessage: string = errorAsString;
    let parsedError = this.tryParseError(error);
    if (parsedError != null) {
      errorMessage = `${parsedError} Date: ${dateString}, Error Message Id:: ${trackingIdString}`;
    }
    this._messageService.add({key: this._key, severity: 'error',sticky:true, summary: summaryMessage, detail: errorMessage });

    const appInsightsMessage = errorAsString + ' Object Detail: ' + serializedObject;
    this.sendToApplicationInsights(appInsightsMessage, trackingIdString);

    console.error(error);
  }

  tryParseError(error: unknown): string | null {
    const payload = error as {
      error?: unknown;
      response?: unknown;
      result?: unknown;
      message?: unknown;
    };

    const candidates: unknown[] = [
      payload.error,
      payload.response,
      payload.result,
      payload.message,
      error
    ];

    for (const candidate of candidates) {
      const parsed = this.tryParseErrorCandidate(candidate);
      if (parsed) {
        return parsed;
      }
    }

    return null;
  }

  private tryParseErrorCandidate(candidate: unknown): string | null {
    if (candidate == null) {
      return null;
    }

    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (!trimmed) {
        return null;
      }

      const parsedJson = this.tryParseJson(trimmed);
      if (parsedJson != null) {
        const fromJson = this.tryParseErrorCandidate(parsedJson);
        if (fromJson) {
          return fromJson;
        }
      }

      return trimmed;
    }

    if (typeof candidate !== 'object') {
      return null;
    }

    const obj = candidate as Record<string, unknown>;

    const directMessage = this.firstNonEmptyString(
      obj['Message'],
      obj['message'],
      obj['detail'],
      obj['title']
    );

    const validationErrors = this.tryParseValidationErrors(obj['errors']);
    if (directMessage && validationErrors) {
      return `${directMessage}: ${validationErrors}`;
    }

    if (validationErrors) {
      return validationErrors;
    }

    if (directMessage) {
      return directMessage;
    }

    // Some APIs nest validation payloads deeper.
    return this.tryParseErrorCandidate(obj['error'])
      ?? this.tryParseErrorCandidate(obj['innerError'])
      ?? this.tryParseErrorCandidate(obj['response']);
  }

  private tryParseValidationErrors(errors: unknown): string | null {
    if (!errors || typeof errors !== 'object') {
      return null;
    }

    const entries = Object.entries(errors as Record<string, unknown>)
      .flatMap(([field, value]) => {
        const values = Array.isArray(value) ? value : [value];
        const messages = values
          .filter((v): v is string => typeof v === 'string')
          .map(v => v.trim())
          .filter(Boolean);

        if (!messages.length) {
          return [];
        }

        const prefix = field ? `${field}: ` : '';
        return [`${prefix}${messages.join(', ')}`];
      });

    return entries.length ? entries.join(' | ') : null;
  }

  private tryParseJson(value: string): unknown | null {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private firstNonEmptyString(...values: unknown[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return null;
  }

  successMessage(summaryMessage: string, detailMessage: string): void {
    this._messageService.clear();
    this._messageService.add({key: this._key, severity: 'success', summary: summaryMessage, detail: detailMessage });
  }

  infoMessage(summaryMessage: string, detailMessage: string): void {
    this._messageService.clear();
    this._messageService.add({key: this._key, severity: 'info', summary: summaryMessage, detail: detailMessage });
  }

  warnMessage(summaryMessage: string, detailMessage: string): void {
    this._messageService.clear();
    this._messageService.add({key: this._key, severity: 'warn', summary: summaryMessage, detail: detailMessage });
  }

  errorNoUiMessage(error: any, summaryMessage: string): void {
    const errorAsString = JSON.stringify(error);
    console.error(error);
  }

  clearMessage(): void {
    this._messageService.clear();
  }

}
